<template>
  <div>
    <b-table
      style="border: 1px solid rgb(0, 0, 0, 0.1); border-radius: 5px; margin-top: 12px"
      :items="vorgangData"
      :fields="vorgangFields"
      :busy="isVorgangLoading"
      responsive
      hover
      head-variant="light"
      @row-clicked="rowClicked"
    >
      <template #cell(id)="row">
        <div class="d-flex align-items-center" style="gap: 6px">
          <span>{{ row.item.id }}</span>
          <GrWunschIcon :teilnehmers="row.item.teilnehmers ?? []" />
        </div>
      </template>
      <template #cell(statusTickets)="row">
        <div v-html="statusTicketsConverter[row.item.statusTickets] || statusTicketsConverter.Critical"></div>
      </template>
      <template #head(thWertGesamt)>
        <span>TH Gesamt</span>
        <span
          class="text-danger"
          title="Zum Zeitpunkt der Buchung"
          v-b-tooltip.hover
          style="align-self: start; position: absolute"
        >
          <Asterisk :size="14" />
        </span>
      </template>
      <template #head(thWertInlandsflug)>
        <span>TH Inlandsflug</span>
        <span
          class="text-danger"
          title="Zum Zeitpunkt der Buchung"
          v-b-tooltip.hover
          style="align-self: start; position: absolute"
        >
          <Asterisk :size="14" />
        </span>
      </template>
      <template #head(thWertMain)>
        <span>TH Langstrecke</span>
        <span
          class="text-danger"
          title="Zum Zeitpunkt der Buchung"
          v-b-tooltip.hover
          style="align-self: start; position: absolute"
        >
          <Asterisk :size="14" />
        </span>
      </template>

      <template #cell(teilnehmerNames)="row">
        {{ kundenName(row.item) }}
      </template>

      <template #cell(thWertMain)="row">
        {{ getThWertBuchung(row.item, 'thMainBuchung') }}
      </template>
      <template #cell(thWertInlandsflug)="row">
        {{ getThWertBuchung(row.item, 'thAlleAddonsBuchung') }}
      </template>
      <template #cell(thWertGesamt)="row">
        {{ getThWertBuchung(row.item, 'thGesamtBuchung') }}
      </template>
      <template #cell(getEarliestTktlFlugbuchung)="row">
        {{ formatTktl(row) }}
      </template>
      <template #cell(aufgaben)="row">
        <b-badge :id="'tooltip-target-vorgang' + row.item.id">
          {{ row.item.aufgaben?.length }}
        </b-badge>
        <b-tooltip
          :target="'tooltip-target-vorgang' + row.item.id"
          triggers="hover"
          custom-class="custom-tooltip"
          v-if="row.item.aufgaben?.length > 1"
        >
          <AufgabenWidget
            :openAufgabenId="openAufgabenId"
            class="mt-3"
            :ansicht="'aufgabe-details'"
            :aufgaben="row.item.aufgaben || []"
          ></AufgabenWidget>
        </b-tooltip>
      </template>
    </b-table>
  </div>
</template>
<script>
import { format, parseISO } from 'date-fns';
import AufgabenWidget from '@/components/produkte/reisetermine/aufgaben-widget.vue';
import GrWunschIcon from '@/components/common/gr-wunsch-icon.vue';
import { Asterisk, Tickets } from 'lucide-vue';
import { lucideHtml } from '@/core/common/helpers/lucide-html';

export default {
  props: {
    vorgangData: { type: Array, required: true },
    openAufgabenId: { type: Number, default: null },
    aufgabenKategorie: { type: String },
  },
  components: { AufgabenWidget, GrWunschIcon, Asterisk },
  data() {
    return {
      isVorgangLoading: false,
      statusTicketsConverter: {
        AllGood: lucideHtml(Tickets, { size: 20, class: 'text-success' }),
        CheckRequired: lucideHtml(Tickets, { size: 20, class: 'text-warning' }),
        Critical: lucideHtml(Tickets, { size: 20, class: 'text-danger' }),
      },
      vorgangDataExpanded: [],
      vorgangFields: [
        {
          tdClass: 'align-middle',
          key: 'id',
          label: 'BNR',
        },
        {
          tdClass: 'align-middle',
          key: 'pnr',
          label: 'PNR',
          formatter: value => value.join(', '),
        },
        {
          tdClass: 'align-middle',
          key: 'teilnehmerNames',
          label: 'Teilnehmernamen',
        },
        // aus den BO-Files
        {
          tdClass: 'align-middle',
          key: 'statusTickets',
          label: 'Status Tickets',
        },
        {
          tdClass: 'align-middle',
          key: 'thWertMain',
          label: 'TH Langstrecke',
        },
        {
          tdClass: 'align-middle',
          key: 'thWertInlandsflug',
          label: 'TH Inlandsflug',
        },
        {
          tdClass: 'align-middle',
          key: 'thWertGesamt',
          label: 'TH Gesamt',
        },
        {
          tdClass: 'align-middle',
          key: 'getEarliestTktlFlugbuchung',
          label: 'TKTL',
        },
        { tdClass: 'align-middle', key: 'aufgaben', label: 'Aufgaben' },
      ],
    };
  },
  computed: {
    jumpIntoVorgangOverview() {
      return (
        this.aufgabenKategorie === 'VorzeitigesTicketing' ||
        this.aufgabenKategorie === 'VorabpruefungTicketBeiGruppe' ||
        this.aufgabenKategorie === 'TicketfreigabeYield' ||
        this.aufgabenKategorie === 'GruppeTicketing' ||
        this.aufgabenKategorie === 'SpecialServices' ||
        this.aufgabenKategorie === 'NegoQuotieren' ||
        this.aufgabenKategorie === 'Individualisierung' ||
        this.aufgabenKategorie === 'Flugbuchung' ||
        this.aufgabenKategorie === 'InlandsflugBuchung' ||
        this.aufgabenKategorie === 'InlandsflugPruefung' ||
        this.aufgabenKategorie === 'InlandsflugAbsprache' ||
        this.aufgabenKategorie === 'Flugpruefung' ||
        this.aufgabenKategorie === 'TicketingAnfrage' ||
        this.aufgabenKategorie === 'FlugplanaenderungAbsprache' ||
        this.aufgabenKategorie === 'SegmenteMismatch'
      );
    },
  },
  methods: {
    getThWertBuchung(vorgang, key) {
      const thWert = vorgang.hotelleistungen?.[0]?.[key];
      return thWert ? this.$options.filters.currency(thWert) : '-';
    },

    kundenName(item) {
      if (item?.kunde?.master) {
        return item?.kunde?.master.vorname + ' ' + item?.kunde?.master.nachname;
      } else return '';
    },
    formatTktl(row) {
      console.log('row: ', row);
      console.log('row.value: ', row.value);

      if (row.value === null || row.value === '') return '-';
      return format(parseISO(row.value.slice(0, -1)), 'dd.MM.yyyy HH:mm');
    },
    rowClicked(item) {
      if (this.jumpIntoVorgangOverview) {
        window.open('/#/vorgaenge/overview?id=' + item.id, '_blank');
        return;
      }

      const reiseterminId = item.hotelleistungen?.[0]?.reisetermin?.id;
      if (reiseterminId == null) return;
      window.open('/#/reisetermine/' + reiseterminId, '_blank');
    },
  },
};
</script>
<style scoped>
:deep(tbody > tr) {
  cursor: pointer;
}

:deep(.b-table td) {
  vertical-align: middle;
}
</style>
<style>
.custom-tooltip .tooltip-inner {
  max-height: 500px;
  overflow-y: auto;
  text-align: left;
}
</style>
